import React, { useEffect, useState } from 'react';
import { toast } from '../lib/toast';
import { apiClient } from '../lib/api';

interface Tier {
  label: string;
  thresholdAmount: number;
  discountPercent: number;
  freeShipping: boolean;
  priorityFulfillment: boolean;
  perks: string[];
}

const blankTier = (): Tier => ({
  label: '',
  thresholdAmount: 0,
  discountPercent: 0,
  freeShipping: false,
  priorityFulfillment: false,
  perks: []
});

const VolumeRewardsAdmin: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await apiClient.getVolumeRewardsConfig();
      if (res.success && res.data) {
        setIsActive(!!res.data.isActive);
        setTiers((res.data.tiers || []).map((t: any) => ({
          label: t.label,
          thresholdAmount: t.thresholdAmount,
          discountPercent: t.discountPercent,
          freeShipping: !!t.freeShipping,
          priorityFulfillment: !!t.priorityFulfillment,
          perks: Array.isArray(t.perks) ? t.perks : []
        })));
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await apiClient.updateVolumeRewardsConfig({ isActive, tiers });
    setSaving(false);
    if (res.success) toast.success('Volume rewards config saved');
    else toast.error(res.error || 'Save failed');
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    const res = await apiClient.recomputeVolumeRewards();
    setRecomputing(false);
    if (res.success && res.data) toast.success(`Recomputed ${res.data.processed} physicians (${res.data.errors} errors)`);
    else toast.error(res.error || 'Recompute failed');
  };

  const updateTier = (idx: number, patch: Partial<Tier>) => {
    setTiers(prev => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const updatePerks = (idx: number, value: string) => {
    const lines = value.split('\n').map(s => s.trim()).filter(Boolean);
    updateTier(idx, { perks: lines });
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold mb-2">Volume Rewards Program</h1>
      <p className="text-sm text-gray-600 mb-6">
        Status-tier program for physicians. Each Mon–Sun week's gross paid invoice subtotal earns a tier
        that takes effect the following week. Admin absorbs discount + free shipping.
      </p>

      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className={`relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
            isActive ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            isActive ? 'translate-x-5' : 'translate-x-0'
          }`} />
        </button>
        <span className="text-sm">{isActive ? 'Program is active' : 'Program is paused'}</span>
      </div>

      <div className="space-y-3 mb-4">
        {tiers.map((t, idx) => (
          <div key={idx} className="border rounded-lg p-4 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
              <label className="block">
                <span className="text-xs text-gray-500">Label</span>
                <input className="border rounded px-2 py-1 w-full" value={t.label}
                  onChange={e => updateTier(idx, { label: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Threshold ($)</span>
                <input type="number" min={0} className="border rounded px-2 py-1 w-full" value={t.thresholdAmount}
                  onChange={e => updateTier(idx, { thresholdAmount: Number(e.target.value) })} />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Discount %</span>
                <input type="number" min={0} max={100} step="0.1" className="border rounded px-2 py-1 w-full" value={t.discountPercent}
                  onChange={e => updateTier(idx, { discountPercent: Number(e.target.value) })} />
              </label>
              <label className="flex items-center gap-2 mt-4">
                <input type="checkbox" checked={t.freeShipping}
                  onChange={e => updateTier(idx, { freeShipping: e.target.checked })} />
                <span className="text-sm">Free shipping</span>
              </label>
              <label className="flex items-center gap-2 mt-4">
                <input type="checkbox" checked={t.priorityFulfillment}
                  onChange={e => updateTier(idx, { priorityFulfillment: e.target.checked })} />
                <span className="text-sm">Priority</span>
              </label>
              <button type="button" className="text-red-600 text-sm hover:underline"
                onClick={() => setTiers(prev => prev.filter((_, i) => i !== idx))}>
                Remove
              </button>
            </div>
            <label className="block mt-3">
              <span className="text-xs text-gray-500">Perks (one per line, display only)</span>
              <textarea
                className="border rounded px-2 py-1 w-full text-sm"
                rows={2}
                value={t.perks.join('\n')}
                onChange={e => updatePerks(idx, e.target.value)}
              />
            </label>
          </div>
        ))}
      </div>

      <button type="button" className="text-sm text-blue-600 hover:underline mb-6"
        onClick={() => setTiers(prev => [...prev, blankTier()])}>
        + Add tier
      </button>

      <div className="flex gap-3">
        <button type="button" onClick={handleSave} disabled={saving}
          className="bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={handleRecompute} disabled={recomputing}
          className="bg-amber-100 text-amber-900 rounded px-4 py-2 hover:bg-amber-200 disabled:opacity-50">
          {recomputing ? 'Running…' : 'Recompute current week'}
        </button>
      </div>
    </div>
  );
};

export default VolumeRewardsAdmin;
